/**
 * Apex outlet grouping rules.
 *
 * The dashboard buckets every outlet returned by the Apex into one of a
 * handful of logical groups so it's glanceable instead of a flat list of
 * 50+ rows. The rules are intentionally name-pattern based — the Apex
 * does not surface module/membership metadata in a stable way.
 *
 * Rules (evaluated in order; first match wins):
 *
 *   displayTank   /^DT_/i               or name === "Moon_lights"
 *   lifeSupport   exact (ci): SKIMMER, HEATER, Sump_Pump, Chill_Pump,
 *                 Fuge_Light, ATO_SOL
 *   varSpeed      /^VarSpd/i
 *   dosing        /^DOS_/i              or /^VO_DOS/i
 *   garage        /^GARAGE_/i
 *   virtualOut    /^VO_/i               (collapsed by default)
 *   other         anything that didn't match above
 *
 * Variable-speed is checked before dosing/virtualOut so a hypothetical
 * `VarSpd_DOS_*` outlet still lands in Variable Speed.
 */
import type { ApexOutletSnapshot } from './types';

export type OutletGroupId =
  | 'displayTank'
  | 'lifeSupport'
  | 'varSpeed'
  | 'dosing'
  | 'garage'
  | 'virtualOut'
  | 'other';

export interface OutletGroupMeta {
  id: OutletGroupId;
  label: string;
  /** Render in this order on the dashboard. */
  order: number;
  /** Initial collapsed state for the section. */
  defaultCollapsed: boolean;
}

export const OUTLET_GROUPS: Record<OutletGroupId, OutletGroupMeta> = {
  displayTank: { id: 'displayTank', label: 'Display Tank', order: 1, defaultCollapsed: false },
  lifeSupport: { id: 'lifeSupport', label: 'Life Support', order: 2, defaultCollapsed: false },
  varSpeed: { id: 'varSpeed', label: 'Variable Speed', order: 3, defaultCollapsed: false },
  dosing: { id: 'dosing', label: 'Dosing', order: 4, defaultCollapsed: false },
  garage: { id: 'garage', label: 'Garage', order: 5, defaultCollapsed: false },
  other: { id: 'other', label: 'Other', order: 6, defaultCollapsed: false },
  virtualOut: { id: 'virtualOut', label: 'Virtual Outputs', order: 7, defaultCollapsed: true },
};

/** Display order for the outlet groups (stable; tests assert on this). */
export const OUTLET_GROUP_ORDER: OutletGroupId[] = Object.values(OUTLET_GROUPS)
  .sort((a, b) => a.order - b.order)
  .map((g) => g.id);

const LIFE_SUPPORT_NAMES = new Set(
  ['SKIMMER', 'HEATER', 'Sump_Pump', 'Chill_Pump', 'Fuge_Light', 'ATO_SOL'].map((n) =>
    n.toLowerCase(),
  ),
);

/**
 * Resolve an outlet name to its display group. Case-insensitive.
 *
 * Outlets that are both physical (rare) and match `/^VO_DOS/i` will be
 * placed in `dosing`, not `virtualOut` — dosing is the more useful
 * grouping for the user.
 */
export function outletGroup(name: string): OutletGroupId {
  const lower = name.toLowerCase();

  if (/^dt_/i.test(name) || lower === 'moon_lights') return 'displayTank';
  if (LIFE_SUPPORT_NAMES.has(lower)) return 'lifeSupport';
  if (/^varspd/i.test(name)) return 'varSpeed';
  if (/^dos_/i.test(name) || /^vo_dos/i.test(name)) return 'dosing';
  if (/^garage_/i.test(name)) return 'garage';
  if (/^vo_/i.test(name)) return 'virtualOut';
  return 'other';
}

/**
 * Partition a list of outlets into their groups. Returns a Map keyed by
 * {@link OutletGroupId} preserving insertion order matching
 * {@link OUTLET_GROUP_ORDER}; empty groups are omitted.
 *
 * Within a group, outlets are sorted by name (case-insensitive) for a
 * deterministic render.
 */
export function groupOutlets(
  outlets: readonly ApexOutletSnapshot[],
): Map<OutletGroupId, ApexOutletSnapshot[]> {
  const buckets = new Map<OutletGroupId, ApexOutletSnapshot[]>();
  for (const id of OUTLET_GROUP_ORDER) buckets.set(id, []);
  for (const o of outlets) {
    const arr = buckets.get(outletGroup(o.name));
    if (arr) arr.push(o);
  }
  for (const arr of buckets.values()) {
    arr.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }
  // Drop empties so callers can simply iterate.
  for (const [id, arr] of buckets) {
    if (arr.length === 0) buckets.delete(id);
  }
  return buckets;
}

/**
 * Alarm-style Apex outlets are read-only by nature — the Apex drives them
 * automatically and the operator cannot (and should not) toggle them. The
 * picker shows a "STATUS" chip instead of RESTRICTED for these.
 *
 * Matches: `Alarm_*`, `SndAlm_*`, `SndWrn_*`, `EmailAlm_*`
 */
export const ALARM_OUTLET_RE = /^(Alarm|SndAlm|SndWrn|EmailAlm)/i;

export function isAlarmStyleOutlet(name: string): boolean {
  return ALARM_OUTLET_RE.test(name);
}

/**
 * Derive a UI-only `'TABLE'` mode for outlets whose `rawState` indicates
 * the Apex is driving them from a TBL profile. Otherwise return the
 * snapshot's reported mode unchanged.
 */
export type DisplayOutletMode = 'AUTO' | 'MANUAL' | 'TABLE';

export function displayOutletMode(outlet: Pick<ApexOutletSnapshot, 'rawState' | 'mode'>): DisplayOutletMode {
  if (outlet.rawState?.toUpperCase() === 'TBL') return 'TABLE';
  return outlet.mode;
}
